import { Text } from '@loomhq/lens';
import classNames from 'classnames';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  TRANSCRIPT_PANEL_MODE_CORRECTION,
  TRANSCRIPT_PANEL_MODE_VIEW,
} from 'reducers/shareVideo';
import {
  TranscriptionState,
  updateHoveredRowIndex,
} from 'reducers/transcription';
import { selectVideoDuration } from 'selectors/videoPlayer';
import { Phrase } from 'utilities/transcript/types';
import { formatTimeStamp } from 'utilities/transcriptList';

import {
  selectEditedRowIndex,
  selectFillerWordHighlight,
  selectHoveredRowIndex,
  selectSelectedElementSource,
  selectTranscriptCorrectionsEnabled,
  selectTranscriptPanelMode,
  selectTranscriptSearchPhrase,
} from '../../../../selectors/transcripts';
import {
  highlightFillerWords,
  makeWordsDetectable,
  replaceSearchWordsInPhrase,
} from '../../../../utilities/transcript/findSearchWordInTranscript';

import styles from '../transcript-list/styles.module.less';

const TranscriptRow = ({
  index,
  currentIndex,
  style,
  setRowHeight,
  phrases,
  onRowClick,
  hasStarted,
  csIndex,
  counter,
}: {
  index: number;
  currentIndex: number;
  style: Record<string, string>;
  setRowHeight: (index: number, height: number) => void;
  phrases: Array<Phrase>;
  onRowClick: (index: number, wordSpan: HTMLSpanElement | null) => void;
  hasStarted: boolean;
  csIndex: number;
  counter: number;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { ts: currentRowTs, value: currentRowValue } = phrases[index];
  const rowRef = useRef(null);
  const panelMode = useSelector(selectTranscriptPanelMode);
  const [isCurrentTs, setIsCurrentTs] = useState(
    currentIndex === index && panelMode === TRANSCRIPT_PANEL_MODE_VIEW
  );
  const duration = useSelector(selectVideoDuration);
  const searchPhrase: string = useSelector(selectTranscriptSearchPhrase);
  const fillerWordHighlight: boolean = useSelector(selectFillerWordHighlight);
  const hoveredRowIndex: number = useSelector(selectHoveredRowIndex);
  const activeIndex = index === csIndex ? counter : -1;
  const shouldShowBaseClass = searchPhrase.length > 0 || fillerWordHighlight;
  const baseClass = shouldShowBaseClass ? `${styles.highlightSearchWord}` : '';
  const editedRowIndex = useSelector(selectEditedRowIndex);
  const editingThisRow = editedRowIndex === index;

  const selectedSourceTag: string = useSelector(selectSelectedElementSource);
  const correctionsEnabled = useSelector<TranscriptionState, boolean>(
    selectTranscriptCorrectionsEnabled
  );

  const shouldMakeWordsDetectable =
    panelMode == TRANSCRIPT_PANEL_MODE_CORRECTION &&
    correctionsEnabled &&
    (index === hoveredRowIndex || editingThisRow);

  const activeClass = styles.active;

  let currentRowValueAdjusted: ReactNode[] = [currentRowValue];

  if (fillerWordHighlight) {
    currentRowValueAdjusted = highlightFillerWords(currentRowValue, baseClass);
  } else if (shouldMakeWordsDetectable) {
    currentRowValueAdjusted = makeWordsDetectable(
      currentRowValue,
      styles.word,
      searchPhrase,
      baseClass,
      activeClass,
      activeIndex,
      // @ts-expect-error ignore due to enabling strict null checks
      phrases[index].ranges,
      selectedSourceTag
    );
  } else if (searchPhrase !== '') {
    // @ts-expect-error ignore due to enabling strict null checks
    currentRowValueAdjusted = replaceSearchWordsInPhrase(
      currentRowValue,
      searchPhrase,
      baseClass,
      activeClass,
      activeIndex
    );
  }

  const classBasedOnIndex = () => {
    if (index === 0) {
      return styles.marginTop;
    }

    if (index === phrases.length - 1) {
      return styles.marginBottom;
    }

    return '';
  };

  // ignore the height unused var that we pull out of style
  const { height: _, ...styleWithoutHeight } = style;

  useEffect(() => {
    setIsCurrentTs(
      currentIndex === index && panelMode === TRANSCRIPT_PANEL_MODE_VIEW
    );
  }, [currentIndex, index, panelMode]);

  useEffect(() => {
    if (rowRef.current) {
      // @ts-expect-error ignore due to enabling strict null checks
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  const handleMouseEnter = () => {
    dispatch(updateHoveredRowIndex(index));
  };

  const handleMouseLeave = () => {
    dispatch(updateHoveredRowIndex(-1));
  };

  const handleMouseMove = () => {
    if (hoveredRowIndex < 0) {
      dispatch(updateHoveredRowIndex(index));
    }
  };

  return (
    <div
      style={styleWithoutHeight}
      className={classNames(styles.container)}
      onMouseUp={evt => {
        onRowClick(index, evt.target as HTMLSpanElement);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      ref={rowRef}
      data-testid={`transcript-row-${index}`}
    >
      <div
        className={classNames(
          styles.row,
          hasStarted && isCurrentTs && styles.highlight,
          classBasedOnIndex()
        )}
      >
        <div className={styles.timestamp}>
          <Text size="small" color="blurple">
            {formatTimeStamp(currentRowTs, duration)}
          </Text>
        </div>
        <div className={styles.transcript}>
          <Text>{currentRowValueAdjusted}</Text>
        </div>
      </div>
    </div>
  );
};

export default TranscriptRow;
